import { useEffect, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Check, X, Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const statusColors = {
    pending_payment: "bg-yellow-100 text-yellow-800",
    pending_approval: "bg-orange-100 text-orange-800",
    approved: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800",
    completed: "bg-blue-100 text-blue-800",
};
const statusLabels = {
    pending_payment: "Väntar betalning",
    pending_approval: "Väntar godkännande",
    approved: "Godkänd",
    rejected: "Avvisad",
    completed: "Slutförd",
};

export default function AdminBookings() {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [selected, setSelected] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    const fetchBookings = () => {
        const params = filter !== "all" ? `?status=${filter}` : "";
        api.get(`/bookings${params}`).then((r) => setBookings(r.data)).catch(console.error).finally(() => setLoading(false));
    };

    useEffect(() => { fetchBookings(); }, [filter]);

    const handleAction = async (id, action) => {
        setActionLoading(true);
        try {
            await api.post(`/bookings/${id}/${action}`);
            toast.success(`Bokning ${action === "approve" ? "godkänd" : "avvisad"}`);
            fetchBookings();
            setSelected(null);
        } catch { toast.error("Åtgärden misslyckades"); }
        finally { setActionLoading(false); }
    };

    return (
        <div className="p-8" data-testid="admin-bookings">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-medium tracking-tight">Bokningar</h1>
                <Select value={filter} onValueChange={setFilter}>
                    <SelectTrigger className="w-48" data-testid="booking-filter">
                        <SelectValue placeholder="Filtrera" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Alla</SelectItem>
                        <SelectItem value="pending_approval">Väntar godkännande</SelectItem>
                        <SelectItem value="approved">Godkända</SelectItem>
                        <SelectItem value="rejected">Avvisade</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {loading ? <div className="text-neutral-400">Laddar...</div> : (
                <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="text-xs uppercase tracking-wider">Namn</TableHead>
                                <TableHead className="text-xs uppercase tracking-wider">Datum</TableHead>
                                <TableHead className="text-xs uppercase tracking-wider">Modell</TableHead>
                                <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                                <TableHead className="text-xs uppercase tracking-wider text-right">Åtgärd</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {bookings.map((b) => (
                                <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                                    <TableCell>
                                        <div className="text-sm font-medium">{b.name}</div>
                                        <div className="text-xs text-neutral-400">{b.email}</div>
                                    </TableCell>
                                    <TableCell className="text-sm">{b.date}</TableCell>
                                    <TableCell className="text-sm">Birò {b.model === "small" ? "Small" : "Big"}</TableCell>
                                    <TableCell>
                                        <Badge className={`text-[10px] ${statusColors[b.status] || "bg-neutral-100"}`}>
                                            {statusLabels[b.status] || b.status}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Button variant="ghost" size="sm" onClick={() => setSelected(b)} data-testid={`view-booking-${b.id}`}>
                                            <Eye size={16} />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!bookings.length && (
                                <TableRow><TableCell colSpan={5} className="text-center text-neutral-400 py-8">Inga bokningar</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Booking Detail Dialog */}
            <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
                <DialogContent className="sm:max-w-lg" data-testid="booking-detail-dialog">
                    <DialogHeader>
                        <DialogTitle>Bokningsdetaljer</DialogTitle>
                    </DialogHeader>
                    {selected && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div><span className="text-neutral-500 block text-xs uppercase tracking-wider mb-1">Namn</span>{selected.name}</div>
                                <div><span className="text-neutral-500 block text-xs uppercase tracking-wider mb-1">E-post</span>{selected.email}</div>
                                <div><span className="text-neutral-500 block text-xs uppercase tracking-wider mb-1">Telefon</span>{selected.phone}</div>
                                <div><span className="text-neutral-500 block text-xs uppercase tracking-wider mb-1">Datum</span>{selected.date}</div>
                                <div><span className="text-neutral-500 block text-xs uppercase tracking-wider mb-1">Modell</span>Birò {selected.model === "small" ? "Small" : "Big"}</div>
                                <div><span className="text-neutral-500 block text-xs uppercase tracking-wider mb-1">Leverans</span>{selected.delivery === "pickup" ? "Nacka" : "Leverans"}</div>
                                <div><span className="text-neutral-500 block text-xs uppercase tracking-wider mb-1">Status</span>
                                    <Badge className={`text-[10px] ${statusColors[selected.status] || "bg-neutral-100"}`}>{statusLabels[selected.status] || selected.status}</Badge>
                                </div>
                                <div><span className="text-neutral-500 block text-xs uppercase tracking-wider mb-1">Körkort</span>{selected.license_file ? "Uppladdad" : "Saknas"}</div>
                            </div>
                            {selected.status === "pending_approval" && (
                                <div className="flex gap-3 pt-4 border-t">
                                    <Button data-testid="approve-booking-btn" onClick={() => handleAction(selected.id, "approve")} disabled={actionLoading} className="flex-1 bg-black text-white">
                                        {actionLoading ? <Loader2 size={14} className="animate-spin mr-1" /> : <Check size={14} className="mr-1" />} Godkänn
                                    </Button>
                                    <Button data-testid="reject-booking-btn" onClick={() => handleAction(selected.id, "reject")} disabled={actionLoading} variant="outline" className="flex-1">
                                        <X size={14} className="mr-1" /> Avvisa
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
